import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {IFeaturedItem, IMenuItem, MenuStore, mergeAndAdd, SessionStore, BRAND, ENGAGEMENT_SOURCE_LOCATION} from '../../store';
import {IBaseResponse, ItemService, WebService} from '../../services';
import {cloneDeep} from 'lodash';

export interface IFeaturedItemStoreState {
	featuredItems: IFeaturedItem[];
}

@Module({name: 'featuredItems', namespaced: true})
class FeaturedItemModule extends VuexModule implements IFeaturedItemStoreState {
	featuredItems: IFeaturedItem[] = [];

	get featuredItem() {
		return (id: string): IFeaturedItem | null => {
			return this.featuredItems.find((featuredItem) => {
				return featuredItem.id === id;
			}) || null;
		};
	}

	/**
	 * This is only used in generic right now and we can inject an updated lineItem
	 * if we have options/name/image on the featured item.
	 */
	get featuredItemList() {
		if(MenuStore.menu && MenuStore.menu.items) {
			const menuItems: IMenuItem[] = [];
			for(const featuredItem of this.featuredItems) {
				const item = MenuStore.item(featuredItem.itemId, featuredItem.itemHqId);
				if(item) {
					// Now modify the lineItem with the featured item data if we have it
					if(featuredItem.options && featuredItem.name && featuredItem.image) {
						const clonedItem = cloneDeep(item);
						const lineItem = ItemService.lineItemGetById({
							itemId: clonedItem.itemId,
							itemHqId: clonedItem.itemHqId,
							itemOptions: featuredItem.options
						});
						if(lineItem) {
							clonedItem.lineItem = lineItem;
							clonedItem.lineItem.engagementSource = ItemService.lineItemEngagementSourceFromFeaturedItem(featuredItem, ENGAGEMENT_SOURCE_LOCATION.FEATURED);
							menuItems.push(clonedItem);
						} else {
							console.warn(`Featured item ${featuredItem.id} has invalid options. Skipping.`);
						}
					} else {
						// Fallback to the original item
						menuItems.push(item);
					}
				}
			}
			return menuItems;
		}
		return [];
	}

	get isFeaturedItemAdvanced() {
		return (featuredItem: IFeaturedItem) => {
			return featuredItem.name && featuredItem.image && Array.isArray(featuredItem.options);
		};
	}

	@Action
	async fetchFeaturedItems(locationId: string) {
		// This request can fire right as the app starts up so it need to wait for
		// the logged in client to be ready.
		await WebService.loggedInStarted;
		const response = await WebService.authenticatedClient.get<IBaseResponse<{featuredItems: IFeaturedItem[]}>>(`locations/${locationId}/featured_items`);
		let featuredItems = response.data.data.featuredItems;
		if(SessionStore.isBrand(BRAND.SEVEN_BREW)) {
			featuredItems = featuredItems.filter(
				(featuredItem) => {
					const isFeaturedItemAdvanced = this.isFeaturedItemAdvanced(featuredItem);
					if(!isFeaturedItemAdvanced) {
						console.warn(`Featured item ${featuredItem.id} is not advanced. Skipping.`);
					}
					return isFeaturedItemAdvanced;
				}
			);
		}
		this.setFeaturedItems(featuredItems);
	}

	@Mutation
	public setFeaturedItems(featuredItems: IFeaturedItem[]) {
		this.featuredItems = featuredItems;
	}

	@Mutation
	public addFeaturedItem(featuredItem: IFeaturedItem) {
		mergeAndAdd(this.featuredItems, [featuredItem], ['id']);
	}

	@Mutation
	public clearFeaturedItems() {
		this.featuredItems = [];
	}
}


export {
	FeaturedItemModule
};
